import React from "react";
import { useTranslation } from "react-i18next";
import { Snowflake, Droplet, Plug, Sparkles, Users, Briefcase, ShieldCheck } from "lucide-react";

const VEHICLES = [
  {
    id: "chr",
    name: "Toyota C-HR Hybride",
    tag: "Bleu Métallisé · Hybride",
    desc: "Notre véhicule signature : hybride éco-responsable, silhouette élégante et habitacle premium pour 1 à 4 passagers.",
    image: "/img/chr-1024.jpg",
    imageWebp: "/img/chr-1024.webp",
    srcsetWebp: "/img/chr-640.webp 640w, /img/chr-1024.webp 1024w, /img/chr-1600.webp 1600w",
    srcsetJpg: "/img/chr-640.jpg 640w, /img/chr-1024.jpg 1024w, /img/chr-1600.jpg 1600w",
    passengers: 4,
    luggage: 3,
    badges: ["Hybride", "Premium"],
    feats: [
      { icon: Snowflake, label: "Climatisation" },
      { icon: Sparkles, label: "Sièges cuir" },
      { icon: Droplet, label: "Eau offerte" },
      { icon: Plug, label: "Chargeurs USB" },
    ],
  },
  {
    id: "corolla",
    name: "Toyota Corolla 2026",
    tag: "Berline · Nouvelle Génération",
    desc: "Notre nouvelle berline 2026 : confort accru, technologie de pointe et habitacle spacieux. Idéale pour les transferts longue distance.",
    image: "/img/corolla-1024.jpg",
    imageWebp: "/img/corolla-1024.webp",
    srcsetWebp: "/img/corolla-640.webp 640w, /img/corolla-1024.webp 1024w, /img/corolla-1600.webp 1600w",
    srcsetJpg: "/img/corolla-640.jpg 640w, /img/corolla-1024.jpg 1024w, /img/corolla-1600.jpg 1600w",
    passengers: 4,
    luggage: 4,
    badges: ["2026", "Nouveau", "Spacieux"],
    feats: [
      { icon: Snowflake, label: "Climatisation" },
      { icon: Sparkles, label: "Intérieur premium" },
      { icon: Droplet, label: "Eau offerte" },
      { icon: ShieldCheck, label: "Safety Sense" },
    ],
  },
];

const VehicleCard = ({ v, index }) => (
  <div
    data-testid={`fleet-vehicle-${v.id}`}
    className="group bg-[#0F1115] border border-white/5 hover:border-[#D4AF37]/40 rounded-2xl overflow-hidden transition-all hover:-translate-y-1"
  >
    <div className="relative aspect-[16/10] overflow-hidden">
      <div className="absolute -inset-4 bg-gradient-to-br from-[#D4AF37]/15 to-transparent blur-2xl" />
      <picture>
        <source type="image/webp" srcSet={v.srcsetWebp} sizes="(min-width: 1024px) 45vw, 100vw" />
        <source type="image/jpeg" srcSet={v.srcsetJpg} sizes="(min-width: 1024px) 45vw, 100vw" />
        <img
          src={v.image}
          alt={`${v.name} - VlaDriver VTC`}
          width="1024"
          height="640"
          loading="lazy"
          decoding="async"
          className="relative w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </picture>
      <div className="absolute top-4 left-4 flex gap-2">
        {v.badges.map((b, i) => (
          <span key={i} className="px-2.5 py-1 rounded-full bg-black/70 backdrop-blur-md text-[10px] uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/30">
            {b}
          </span>
        ))}
      </div>
    </div>

    <div className="p-5">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="font-editorial text-xl md:text-2xl font-light text-white tracking-tight">{v.name}</h3>
        <span className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80 shrink-0">{v.tag.split(" · ")[0]}</span>
      </div>

      <div className="flex gap-4 mb-4 text-xs text-white/60">
        <span className="inline-flex items-center gap-1.5"><Users size={13} strokeWidth={1.8} className="text-[#D4AF37]" /> {v.passengers} pax</span>
        <span className="inline-flex items-center gap-1.5"><Briefcase size={13} strokeWidth={1.8} className="text-[#D4AF37]" /> {v.luggage} bagages</span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {v.feats.map((f, i) => (
          <span key={i} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/60 bg-white/[0.03] border border-white/8 rounded-full px-2.5 py-1">
            <f.icon size={11} strokeWidth={1.8} className="text-[#D4AF37]" />
            {f.label}
          </span>
        ))}
      </div>
    </div>
  </div>
);

const Fleet = () => {
  const { t } = useTranslation();

  return (
    <section data-testid="fleet-section" className="py-14 md:py-20 bg-[#0A0B0D] relative overflow-hidden">
      <div className="absolute inset-0 bg-grain opacity-30" />
      <div className="container mx-auto px-6 lg:px-12 relative">
        <div className="max-w-2xl mb-8">
          <div className="inline-flex items-center gap-4 mb-4">
            <span className="block w-10 h-px bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase font-medium">Flotte</span>
          </div>
          <h2 className="font-editorial text-3xl md:text-4xl font-light text-white tracking-tight">{t("fleet.title")}</h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-5 md:gap-6">
          {VEHICLES.map((v, i) => <VehicleCard key={v.id} v={v} index={i} />)}
        </div>
      </div>
    </section>
  );
};

export default Fleet;
